import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Alert,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { routes } from '../config';
import { AuthShell } from '@/components/AuthShell';

interface ReferralCodeProps {
  onBack: () => void;
}

export default function ReferralCode({ onBack }: ReferralCodeProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [referralCode, setReferralCode] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!referralCode.trim()) {
      setError('Please enter a referral code');
      return;
    }
    
    setError('');
    // Navigate back to register with the referral code
    navigate(routes.register, { 
      state: { referralCode: referralCode.trim() },
      replace: true 
    });
  };

  const formContent = (
    <Box component="form" onSubmit={handleSubmit}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <IconButton onClick={onBack} sx={{ color: '#666' }}>
          <ArrowBack />
        </IconButton>
        
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
          Add Referral Code
        </Typography>
        
        <Box sx={{ width: 40 }} /> {/* Spacer for centering */}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Illustration */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        mb: 3,
        height: '140px',
        borderRadius: 2,
        alignItems: 'center'
      }}>
        <img 
          src="/images/img_referral_code.svg" 
          alt="Referral Code Illustration" 
          style={{ 
            height: '180px', 
            width: 'auto',
            maxWidth: '100%'
          }} 
        />
      </Box>

      {/* Instruction */}
      <Typography variant="body1" sx={{ textAlign: 'center', mb: 3, color: '#666' }}>
        If you have a referral code, please enter it below to claim reward(s)
      </Typography>

      {/* Input Field */}
      <TextField
        fullWidth
        placeholder="Enter referral code"
        value={referralCode}
        onChange={(e) => setReferralCode(e.target.value)}
        sx={{
          mb: 3,
          '& .MuiOutlinedInput-root': {
            borderRadius: '99px',
            backgroundColor: '#F6F6F6',
            border: `1px solid ${theme.palette.secondary.light}`,
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '& .MuiInputBase-input': {
              padding: '10px 16px',
              height: '42px',
              boxSizing: 'border-box',
            },
          },
          '& .MuiInputBase-input::placeholder': {
            color: '#999',
            opacity: 1,
          },
        }}
      />

      {/* Submit Button */}
      <Button
        type="submit"
        fullWidth
        variant="contained"
        sx={{
          height: '44px',
          borderRadius: '99px',
          padding: '10px 16px',
          textTransform: 'none',
          backgroundColor: theme.palette.secondary.main,
          color: 'white',
          '&:hover': {
            backgroundColor: theme.palette.secondary.dark,
          },
          fontSize: '16px',
          fontWeight: 600,
        }}
      >
        Submit
      </Button>
    </Box>
  );

  return <AuthShell showLangSelector={false}>{formContent}</AuthShell>;
}
